/**
 * Shared constants for the public Loan Application.
 *
 * The field NAMES in this form are a wire contract with
 * `POST borrower_submissions/new_application` — they must match what the backend
 * (and the queue that consumes these submissions) expects. Where a name is
 * misspelled upstream (`exisitingLender`) the misspelling is preserved
 * deliberately; only the human-facing label is corrected.
 */

// Loan product types that finance a construction/renovation project, and so ask
// for a draw budget rather than a single loan amount.
export const CONSTRUCTION_PRODUCTS = ["Fix and Flip", "Ground Up Construction"];

// Loan purposes that pay off an existing loan, and so ask for payoff details.
export const REFINANCE_PURPOSES = [
  "Refinance - No Cash Out",
  "Refinance - Cash Out",
];

/**
 * Loan-related contacts are three fixed slots rendered by array index; the
 * `loanRole` is stamped on at submit time from this list rather than collected
 * as a field. Order is load-bearing — index 0 is the appraisal contact, etc.
 */
export const LOAN_CONTACT_ROLES = [
  "Appraisal Point of Contact",
  "Title Agent",
  "Insurance Agent",
];

/**
 * Every remaining category implies a US primary address, so this form has no
 * non-US address branch. ("Foreign National Non-Resident" and "Other - Options
 * do not apply" were the only two that didn't, and both were dropped.) If a
 * non-US category is ever reintroduced, the `nonUSborrowerAddress` field needs
 * to come back with it.
 */
export const CITIZENSHIP_OPTIONS = [
  "US Citizen",
  "Foreign National with SSN",
  "Foreign National",
];

export const PRIMARY_RESIDENCE_OWNERSHIP_OPTIONS = ["Own", "Rent"];

/**
 * Housing events, relabelled for this form only.
 *
 * `value` is the stored string and matches the canonical housing-event list
 * exactly — the borrower record and the application PDF both read these values.
 * Only the display label differs, so renaming here can't strand an existing
 * borrower whose record says "Loan Modifications".
 */
export const HOUSING_EVENT_OPTIONS = [
  { value: "Deed in lieu", label: "Deed in lieu" },
  { value: "Foreclosure", label: "Foreclosure" },
  { value: "Forbearance", label: "Forbearance" },
  { value: "Short Sales", label: "Short Sales" },
  { value: "Lis Pendens", label: "Lis Pendens" },
  { value: "Loan Modifications", label: "Mortgage Loan Modifications" },
];

/**
 * Prior-experience categories and the count rows each one reveals.
 *
 * `rows[].name` is the form-root key the counts are written under
 * (values.ff_sold = { less_than_three_years, greater_than_three_years }) — the
 * shape `new_application` expects. The UI presentation is ours; the payload is
 * not.
 */
export const EXPERIENCE_CATEGORIES = [
  {
    flag: "hasExperienceWithIncomeProducingProperties",
    label: "Income Producing Properties",
    rows: [
      {
        name: "income_producing_properties",
        label: "Income Producing Properties acquired",
      },
    ],
  },
  {
    flag: "hasExperienceWithFixFlips",
    label: "Fix & Flips",
    rows: [
      { name: "ff_sold", label: "Fix and flips completed and sold" },
      {
        name: "ff_retained_as_rental",
        label: "Fix and flips completed and retained as rental",
      },
    ],
  },
  {
    flag: "hasExperienceWithGroundUpConstruction",
    label: "Ground Up Construction",
    rows: [
      { name: "guc_sold", label: "Ground up construction - completed and sold" },
      {
        name: "guc_retained_as_rental",
        label: "Ground up construction - retained as rental",
      },
      {
        name: "general_contractor_projects",
        label: "Rehab completed and sold as GC for 3rd party investor",
      },
    ],
  },
];

/** Every experience count key, flattened — used to clear counts on deselect. */
export const ALL_EXPERIENCE_ROW_NAMES = EXPERIENCE_CATEGORIES.flatMap((c) =>
  c.rows.map((r) => r.name),
);

export const FICO_OPTIONS = [
  "Below 600",
  "600-620",
  "620-650",
  "650-680",
  "680-700",
  "700-720",
  "Greater than 720",
  "N/A do not have FICO",
];

export const TIME_SINCE_HOUSING_EVENT_OPTIONS = [
  "Within the last 12-months",
  "Within the last 2-years",
  "Greater than 2-years ago",
];

export const FF_PROPERTY_STATUS_OPTIONS = [
  "Requires significant renovation",
  "Average condition - requires moderate renovation",
  "Above Average condition - requires limited renovation",
];

export const GUC_PROPERTY_STATUS_OPTIONS = [
  "Raw land without approved plans and/or utilities",
  "Raw land with approved plans and utilities in place",
  "Foundation in place",
  "Significant Progress to Exterior, limited or no exterior work complete",
  "Finishing Interior",
];

export const DEFAULT_TYPE_OPTIONS = ["Maturity Default", "Payment Default"];

export const SUBJECT_PROPERTY_COUNT_OPTIONS = ["1", "2", "3", "4+ properties"];

/**
 * The authorization agreement, one paragraph per entry.
 *
 * Carried over verbatim — this is a legal consent, so the wording is not ours to
 * edit. The credit-authorization form has a near-identical agreement differing
 * only in its final paragraph; see creditAuthShared.js.
 */
export const AGREEMENT_PARAGRAPHS = [
  "Applicant attests that this application is for financing of a business purpose loan and any subject properties covered under this application are not owner occupied, used as a second home or vacation property, and any recent use of the property as a primary residence will require supporting evidence that all guarantors and their family members are no longer occupying the residence.",
  "The actual terms and conditions upon which the Lender might extend credit are subject to satisfactory completion of due diligence, internal approval, satisfactory review of documentation and such other or different items and conditions as may be determined by the Lender following the completion of its underwriting (the “Underwriting and Approval Process”). As a result, notwithstanding any agreement of the parties to the contrary, (i) Lender’s approval of the loan documents shall not constitute a final approval of the loan and (ii) the loan documents shall not be considered final until the Underwriting and Approval Process is completed (as determined in Lender’s sole discretion).",
  "The borrower hereby agrees that all aforementioned information is true and in no means is false and intended to influence the credit underwriting of the Lender in regard to origination of the loan associated with the subject property or properties. Additionally, the borrower understands that this loan application does not constitute a commitment from the Lender to originate the loan associated with the subject property or properties. Lastly, the borrower acknowledges that all fees collected in relation to loan due diligence are non-refundable, in any circumstance.",
  "If necessary, I hereby authorize and instruct the Lender and its vendors/affiliates to obtain and review my personal credit report, background checks, and all other reports and documents and involving due diligence in connection with my real estate loan. I understand and agree that the Lender and its vendors/affiliates intends to use the credit report for the purpose of evaluating my financial readiness to personally guarantee the real estate loan that I have applied for. My signature below authorizes the release to credit report and screening agencies of financial or other information that I have supplied to the Lender and its vendors/affiliates in connection with such evaluation. Authorization is further granted to the credit report and screening agencies to use a copy of this form to obtain any information the agencies may deem necessary to complete my credit report and/or background screenings.",
];
